import * as React from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

// Beta typed wrapper for supabase.auth.oauth (three methods we call).
type OAuthClient = { name?: string | null; client_name?: string | null };
type AuthorizationDetails = {
  client?: OAuthClient | null;
  redirect_url?: string | null;
  redirect_to?: string | null;
  scope?: string | null;
  scopes?: string[] | null;
};
type OAuthResult = {
  data: AuthorizationDetails | null;
  error: { message: string } | null;
};
type AuthOAuth = {
  getAuthorizationDetails: (id: string) => Promise<OAuthResult>;
  approveAuthorization: (id: string) => Promise<OAuthResult>;
  denyAuthorization: (id: string) => Promise<OAuthResult>;
};
function oauthApi(): AuthOAuth {
  return (supabase.auth as unknown as { oauth: AuthOAuth }).oauth;
}

type ConsentSearch = { authorization_id: string };

export const Route = createFileRoute("/.lovable/oauth/consent")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>): ConsentSearch => ({
    authorization_id:
      typeof s.authorization_id === "string" ? s.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      const next = location.pathname + location.searchStr;
      throw redirect({
        to: "/auth",
        search: { mode: "signin", redirect: next },
      });
    }
  },
  loader: async ({ location }) => {
    const authorizationId =
      new URLSearchParams(location.search).get("authorization_id") ?? "";
    const { data, error } = await oauthApi().getAuthorizationDetails(
      authorizationId,
    );
    if (error) throw error;
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    return data;
  },
  head: () => ({
    meta: [
      { title: "Authorize access: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ConsentPage,
  errorComponent: ({ error }) => (
    <ConsentShell>
      <h1 className="mb-3 text-[20px] font-semibold text-brand-ink">
        Could not load this authorization request
      </h1>
      <p className="text-[14px] text-brand-muted">
        {String((error as Error)?.message ?? error)}
      </p>
    </ConsentShell>
  ),
});

function ConsentShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-brand-paper-2 px-4 py-12 sm:py-16">
      <article className="mx-auto max-w-[460px] overflow-hidden rounded-2xl border border-brand-line bg-brand-paper shadow-[0_1px_0_var(--brand-line),0_30px_60px_-30px_oklch(0.264_0.068_255.3/0.18)]">
        <div className="px-[28px] pt-10 pb-9">
          <div className="mb-7 flex flex-col items-center text-center">
            <span className="font-sans text-[15px] font-semibold tracking-tight text-brand-ink">
              Capital<span className="text-brand-accent">Dart</span>
            </span>
          </div>
          {children}
        </div>
      </article>
    </main>
  );
}

function ConsentPage() {
  const details = Route.useLoaderData();
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [userEmail, setUserEmail] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserEmail(data.user?.email ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const clientName =
    details?.client?.name ?? details?.client?.client_name ?? "an assistant";
  const scopes: string[] = Array.isArray(details?.scopes)
    ? (details?.scopes as string[])
    : typeof details?.scope === "string" && details.scope.length > 0
      ? details.scope.split(/\s+/)
      : [];

  /**
   * Attribution only, failure-isolated: when the account was created during
   * an assistant connect and no client was recorded yet, append the client
   * name (e.g. assistant_connect:ChatGPT). Never blocks the OAuth flow.
   */
  async function recordAttribution() {
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      const name = details?.client?.name ?? details?.client?.client_name ?? "";
      if (!uid || !name) return;
      const { data: prof } = await supabase
        .from("profiles")
        .select("signup_source")
        .eq("user_id", uid)
        .maybeSingle();
      const source = (prof as { signup_source?: string | null } | null)
        ?.signup_source;
      if (source !== "assistant_connect") return;
      await supabase
        .from("profiles")
        .update({ signup_source: `assistant_connect:${name}` } as never)
        .eq("user_id", uid);
    } catch {
      // attribution must never block auth
    }
  }

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const api = oauthApi();
    const { data, error: apiError } = approve
      ? await api.approveAuthorization(authorization_id)
      : await api.denyAuthorization(authorization_id);
    if (apiError) {
      setBusy(false);
      setError(apiError.message);
      return;
    }
    if (approve) await recordAttribution();

    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  return (
    <ConsentShell>
      <h1 className="mb-2 text-[20px] font-semibold text-brand-ink">
        Connect {clientName} to CapitalDart
      </h1>
      <p className="mb-5 text-[14px] text-brand-muted">
        {clientName} is asking to access CapitalDart as you
        {userEmail ? ` (${userEmail})` : ""}. If you approve, it can call the
        CapitalDart tools that are enabled for your account, acting on your
        behalf.
      </p>

      <div className="mb-5 rounded-lg border border-brand-line bg-brand-paper-2 p-4">
        <p className="mb-2 text-[13px] font-medium text-brand-ink">
          What is being granted
        </p>
        <ul className="list-disc space-y-1 pl-5 text-[13px] text-brand-muted">
          <li>Sign in as your CapitalDart account for this connection.</li>
          <li>Read and act on your CapitalDart data through enabled tools.</li>
          <li>
            Your existing CapitalDart permissions and backend policies still
            apply. This does not grant admin access or bypass any rules.
          </li>
        </ul>
        {scopes.length > 0 && (
          <p className="mt-3 text-[12px] text-brand-muted">
            Requested scopes: {scopes.join(", ")}
          </p>
        )}
      </div>

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
        >
          {error}
        </p>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => decide(false)}
        >
          Cancel connection
        </Button>
        <Button type="button" disabled={busy} onClick={() => decide(true)}>
          {busy ? "Working..." : "Approve"}
        </Button>
      </div>

      <p className="mt-5 text-[12px] text-brand-muted">
        You can revoke this connection later from your CapitalDart account
        settings.
      </p>
    </ConsentShell>
  );
}